import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

/**
 * PO-3 / Tracker #24: auto-calculate Payment Amount per stage.
 *
 * For each row in `payment_terms`, payment_amount = invoice_portion% × total_amount.
 * `invoice_portion` is free-text the user types as a percentage ("10", "10%", "90 %").
 * Computed amounts are rounded to 2 decimals; the field is read-only in the form.
 */

const parsePercent = (raw: unknown): number | null => {
    if (raw === null || raw === undefined || raw === '') return null;
    // Strip everything except digits and decimal point (handles "10%", "10 %", "₹10").
    const cleaned = String(raw).replace(/[^0-9.]/g, '');
    if (cleaned === '') return null;
    const pct = parseFloat(cleaned);
    return Number.isFinite(pct) ? pct : null;
};

export const PaymentSplitWatcher = () => {
    const { setValue } = useFormContext();
    const total = useWatch({ name: 'total_amount' });
    const paymentTerms = useWatch({ name: 'payment_terms' });

    // Re-run only when the total or any invoice_portion actually changes.
    const portionsKey = Array.isArray(paymentTerms)
        ? paymentTerms.map((t: any) => t?.invoice_portion ?? '').join('|')
        : '';

    useEffect(() => {
        if (!Array.isArray(paymentTerms) || paymentTerms.length === 0) return;
        const totalAmount = parseFloat(String(total ?? '0')) || 0;

        paymentTerms.forEach((term: any, index: number) => {
            const pct = parsePercent(term?.invoice_portion);
            if (pct === null) return;
            const amount = Math.round(((totalAmount * pct) / 100) * 100) / 100;
            if (term?.payment_amount !== amount) {
                setValue(`payment_terms.${index}.payment_amount`, amount, {
                    shouldDirty: true,
                    shouldValidate: false,
                });
            }
        });
    }, [total, portionsKey, paymentTerms, setValue]);

    return null;
};
